import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { API } from "../App";

const DEFAULT_LEHRGANG_OPTIONS = [
  "J-Lehrgang", "D-Lehrgang", "C-Lehrgang", "B/K-Lehrgang",
  "B-Lehrgang", "D-Fortbildung", "C-Fortbildung", "B-Fortbildung",
];
const DEFAULT_MANNSCHAFT_OPTIONS = [
  "Damen 1", "Damen 2", "Herren 1", "Herren 2", "Herren 3", "Herren 4", "Jugend",
];

export function useData(sessionId) {
  const [data, setData] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [licenseTypes, setLicenseTypes] = useState({ geschlechter: [], spieler_lizenzen: [], schiedsrichter_lizenzen: [], gueltig_bis_jahre: [] });
  const [columnConfig, setColumnConfig] = useState([]);
  const [sourceStatus, setSourceStatus] = useState({ spieler_exists: true, schiedsrichter_exists: true });
  const [appSettings, setAppSettings] = useState({
    app_title: "Lizenzen",
    dashboard_title: "Lizenz-Übersicht",
    mannschaft_options: DEFAULT_MANNSCHAFT_OPTIONS,
    lehrgang_options: DEFAULT_LEHRGANG_OPTIONS,
  });
  const [sorting, setSorting] = useState(false);
  const [uploadType, setUploadType] = useState("spieler");
  const [uploading, setUploading] = useState(false);
  const [editingTitle, setEditingTitle] = useState(null);

  const MANNSCHAFT_OPTIONS = appSettings.mannschaft_options?.length > 0
    ? appSettings.mannschaft_options : DEFAULT_MANNSCHAFT_OPTIONS;
  const LEHRGANG_OPTIONS = appSettings.lehrgang_options?.length > 0
    ? appSettings.lehrgang_options : DEFAULT_LEHRGANG_OPTIONS;

  const columns = useMemo(() => {
    // Filter out 'actions' from config (it's always auto-appended)
    const visibleCols = columnConfig.filter((col) => col.visible && col.key !== 'actions');
    return [...visibleCols, { key: "actions", label: "", visible: true }];
  }, [columnConfig]);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setAppSettings(response.data);
    } catch (err) {
      console.error("Error fetching settings:", err);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [dataRes, typesRes, colRes, statusRes] = await Promise.all([
        axios.get(`${API}/data?session_id=${sessionId}`),
        axios.get(`${API}/data/license-types?session_id=${sessionId}`),
        axios.get(`${API}/columns?session_id=${sessionId}`),
        axios.get(`${API}/data/source-status?session_id=${sessionId}`),
      ]);
      setData(dataRes.data.data || []);
      setLastUpdated(dataRes.data.last_updated);
      setLicenseTypes(typesRes.data);
      setColumnConfig(colRes.data.columns || []);
      setSourceStatus(statusRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchData();
    fetchSettings();
  }, [fetchData, fetchSettings]);

  const saveInlineTitle = async (key, value) => {
    try {
      await axios.put(`${API}/settings?session_id=${sessionId}`, { [key]: value });
      setAppSettings((prev) => ({ ...prev, [key]: value }));
      setEditingTitle(null);
    } catch (err) {
      console.error("Error saving title:", err);
    }
  };

  const handleExport = async (filters, sortConfig) => {
    try {
      const params = new URLSearchParams({
        session_id: sessionId,
        filter_geschlecht: filters.geschlecht,
        filter_spieler: filters.spieler,
        filter_sr: filters.sr,
        filter_gueltig_bis: filters.gueltigBis,
        sort_key: sortConfig.key,
        sort_direction: sortConfig.direction,
      });
      const response = await axios.get(`${API}/data/export?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `lizenzen_export_${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export error:", err);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    const endpoint = uploadType === "spieler"
      ? `${API}/admin/upload-spieler`
      : `${API}/admin/upload-schiedsrichter`;
    try {
      setUploading(true);
      await axios.post(`${endpoint}?session_id=${sessionId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      fetchData();
      return true;
    } catch (err) {
      return false;
    } finally {
      setUploading(false);
    }
  };

  const handleClearTable = async () => {
    const c1 = window.confirm("WARNUNG: Die gesamte Tabelle wird unwiderruflich gelöscht!\n\nSind Sie sicher?");
    if (!c1) return;
    const c2 = window.confirm("Diese Aktion kann nicht rückgängig gemacht werden.\n\nWirklich fortfahren?");
    if (!c2) return;
    try {
      await axios.delete(`${API}/admin/clear-table?session_id=${sessionId}`);
      fetchData();
    } catch (err) {
      console.error("Clear table error:", err);
    }
  };

  const handleSort = (key) => {
    setSorting(true);
    setTimeout(() => setSorting(false), 400);
    return key;
  };

  return {
    data, lastUpdated, loading, licenseTypes, columns, columnConfig, sourceStatus,
    appSettings, editingTitle, setEditingTitle, sorting, uploadType, setUploadType,
    uploading, MANNSCHAFT_OPTIONS, LEHRGANG_OPTIONS,
    fetchData, fetchSettings, saveInlineTitle, handleExport, handleFileUpload,
    handleClearTable, handleSort,
  };
}
